// components/dashboard/ResourceCard.jsx
import { FileText, Video, Music, Link, Download, Tag } from "lucide-react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next"; // ← اضافه شده
import { BASE_URL, CDN_BASE_URL } from "../../config";

// Define custom icons for different resource types
const typeIcons = {
  pdf: <FileText className="text-red-500 w-6 h-6" />,
  word: <FileText className="text-blue-500 w-6 h-6" />,
  video: <Video className="text-purple-600 w-6 h-6" />,
  audio: <Music className="text-green-500 w-6 h-6" />,
  default: <FileText className="text-gray-400 w-6 h-6" />,
};

const ResourceCard = ({ resource }) => {
  const { t, i18n } = useTranslation(); // ← اضافه شده
  const isRtl = i18n.language.includes("fa"); // ← برای تشخیص زبان فارسی

  const url = resource.url.startsWith("http")
    ? resource.url
    : `${CDN_BASE_URL}${resource.url}`;

  // Helper to format date based on language
  const formattedDate = resource.createdAt
    ? new Date(resource.createdAt).toLocaleDateString(
        i18n.language === "fa" ? "fa-IR" : "en-US",
        {
          year: "numeric",
          month: "long",
          day: "numeric",
        }
      )
    : t("resource_card.unknown_date");

  // Determine if it's a direct download or needs to be viewed
  const isDownloadable = ["pdf", "word", "audio"].includes(resource.type);

  const actionButtonText = isDownloadable
    ? t("resource_card.download_resource")
    : t("resource_card.view_resource");
  const tagText = resource.category || t("resource_card.no_category");

  return (
    <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 flex flex-col justify-between border border-gray-100 transform hover:-translate-y-1">
      <div>
        {/* Resource Type Icon & Title */}
        <div className={`flex items-center gap-3 mb-3 `}>
          <div className="flex-shrink-0">
            {typeIcons[resource.type] || typeIcons.default}
          </div>
          <h3 className="text-xl font-bold text-gray-900 leading-tight line-clamp-2">
            {resource.title}
          </h3>
        </div>

        {/* --- بخش جدید و بهبود یافته دسته‌بندی --- */}
        <div
          className={`flex items-center text-sm font-semibold text-gray-700 mb-4 mt-1 `}
        >
          <Tag
            className={`w-4 h-4 text-gray-500 ${isRtl ? "ml-1.5" : "mr-1.5"}`}
          />
          <span className="bg-blue-600 px-3 py-1 rounded-full text-xs font-medium text-white tracking-wide">
            {tagText}
          </span>
        </div>
        {/* ------------------------------------------- */}

        {/* Resource Description (Optional, but good to have) */}
        {resource.description && (
          <p className="text-sm text-gray-600 mb-3 line-clamp-3">
            {resource.description}
          </p>
        )}

        {/* Date Added */}
        <div className={`flex items-center text-sm text-gray-500 mt-4 `}>
          <svg
            className={`w-4 h-4 text-gray-400 ${isRtl ? "ml-1" : "mr-1"}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            ></path>
          </svg>
          <span className="font-medium">{t("resource_card.added_date")}:</span>{" "}
          <span className={`${isRtl ? "mr-1" : "ml-1"}`}>{formattedDate}</span>
        </div>
      </div>

      {/* Action Button */}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={`mt-6 flex items-center justify-center bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 transition-colors duration-200 text-base font-semibold text-center shadow-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transform `}
      >
        {isDownloadable ? (
          <Download className={`w-5 h-5 ${isRtl ? "ml-2" : "mr-2"}`} />
        ) : (
          <Link className={`w-5 h-5 ${isRtl ? "ml-2" : "mr-2"}`} />
        )}
        {actionButtonText}
      </a>
    </div>
  );
};

ResourceCard.propTypes = {
  resource: PropTypes.shape({
    title: PropTypes.string.isRequired,
    type: PropTypes.oneOf(["pdf", "word", "video", "audio"]).isRequired,
    category: PropTypes.string,
    createdAt: PropTypes.string,
    url: PropTypes.string.isRequired,
    description: PropTypes.string,
  }).isRequired,
};

export default ResourceCard;
